import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { fmtDate, donorName } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Plus, AlertTriangle, HandCoins, Timer } from "lucide-react";

export default function FollowUp() {
  const qc = useQueryClient();
  const { data: smart } = useQuery({
    queryKey: ["smart-lists"],
    queryFn: async () => (await api.get("/smart-lists")).data,
  });
  const { data: open = [] } = useQuery({
    queryKey: ["followups-open"],
    queryFn: async () => (await api.get("/followups", { params: { only_open: true } })).data,
  });
  const { data: donors = [] } = useQuery({
    queryKey: ["donors-lite"],
    queryFn: async () => (await api.get("/donors")).data,
  });

  const [dlgOpen, setDlgOpen] = useState(false);
  const [form, setForm] = useState({ donor_id: "", due_date: new Date().toISOString().slice(0, 10), note: "" });
  const create = useMutation({
    mutationFn: async () => (await api.post("/followups", form)).data,
    onSuccess: () => { toast.success("Follow-up creato"); setDlgOpen(false); setForm({ donor_id: "", due_date: new Date().toISOString().slice(0, 10), note: "" }); qc.invalidateQueries({ queryKey: ["followups-open"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); },
  });
  const complete = useMutation({
    mutationFn: async (id) => (await api.patch(`/followups/${id}`, { done: true })).data,
    onSuccess: () => { toast.success("Completato"); qc.invalidateQueries({ queryKey: ["followups-open"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); },
  });

  const sections = [
    { key: "in_ritardo", title: "Donatori in ritardo", icon: AlertTriangle, tone: "danger", list: smart?.in_ritardo || [] },
    { key: "da_ringraziare", title: "Da ringraziare", icon: HandCoins, tone: "success", list: smart?.da_ringraziare || [] },
    { key: "fermi_30", title: "Fermi 30+ giorni", icon: Timer, tone: "warn", list: smart?.fermi_30 || [] },
    { key: "fermi_60", title: "Fermi 60+ giorni", icon: Timer, tone: "warn", list: smart?.fermi_60 || [] },
    { key: "fermi_90", title: "Fermi 90+ giorni", icon: Timer, tone: "warn", list: smart?.fermi_90 || [] },
  ];

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] mx-auto">
      <PageHeader
        title="Follow-up"
        subtitle="Liste smart per organizzare i contatti quotidiani."
        actions={
          <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
            <DialogTrigger asChild>
              <Button data-testid="followup-add-btn" className="rounded-full bg-[#1C352D] hover:bg-[#264A3E]">
                <Plus size={14} className="mr-1.5"/> Nuovo reminder
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nuovo follow-up</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Donatore</Label>
                  <Select value={form.donor_id} onValueChange={(v) => setForm({ ...form, donor_id: v })}>
                    <SelectTrigger className="mt-1.5"><SelectValue placeholder="Scegli donatore"/></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {donors.map((d) => (
                        <SelectItem key={d.id} value={d.id}>{donorName(d)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Data promemoria</Label>
                  <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1.5"/>
                </div>
                <div>
                  <Label>Nota / azione</Label>
                  <Textarea rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1.5"/>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDlgOpen(false)}>Annulla</Button>
                <Button className="bg-[#1C352D] hover:bg-[#264A3E]" onClick={() => create.mutate()}>Salva</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <Card className="p-6 border border-stone-200 rounded-xl shadow-none mb-6">
        <div className="text-sm font-medium mb-3">Reminder aperti ({open.length})</div>
        {open.length === 0 && <div className="text-xs text-stone-500">Nessun reminder aperto.</div>}
        <ul className="divide-y divide-stone-100">
          {open.map((f) => (
            <li key={f.id} className="py-3 flex items-center justify-between">
              <div>
                <Link to={`/donatori/${f.donor_id}`} className="font-medium hover:underline">{f.donor_name}</Link>
                <div className="text-xs text-stone-500">Scadenza {fmtDate(f.due_date)} · {f.note}</div>
              </div>
              <Button size="sm" variant="outline" className="rounded-full text-xs" data-testid={`followup-complete-${f.id}`} onClick={() => complete.mutate(f.id)}>
                <CheckCircle2 size={13} className="mr-1"/> Completa
              </Button>
            </li>
          ))}
        </ul>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {sections.map((s) => {
          const Ic = s.icon;
          const toneMap = { danger: "text-red-700 bg-red-50", success: "text-emerald-700 bg-emerald-50", warn: "text-orange-700 bg-orange-50" };
          return (
            <Card key={s.key} className="p-6 border border-stone-200 rounded-xl shadow-none">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-lg grid place-items-center ${toneMap[s.tone]}`}><Ic size={15}/></div>
                  <div className="text-sm font-medium">{s.title}</div>
                </div>
                <div className="text-xs text-stone-500">{s.list.length}</div>
              </div>
              {s.list.length === 0 ? (
                <div className="text-xs text-stone-500">Nessun donatore in questa lista.</div>
              ) : (
                <ul className="divide-y divide-stone-100">
                  {s.list.slice(0, 6).map((d) => (
                    <li key={d.id} className="py-2 text-sm flex items-center justify-between">
                      <Link to={`/donatori/${d.id}`} className="font-medium hover:underline">{donorName(d)}</Link>
                      <span className="text-[11px] text-stone-500">{fmtDate(d.last_payment_date)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
